/**
 * Existing game-launch.js bundles call `navigator.wakeLock.request('screen')` and
 * await it during boot. On iOS Safari that often rejects with "Permission was denied"
 * (no transient user gesture), which aborts launch while the loading screen is up.
 *
 * Patch once in the host launcher so unrebuilt slot dists keep working.
 */

let installed = false;

const PATCHED_WAKE_LOCK = Symbol('atemoyaPatchedWakeLock');

function createReleasedSentinel(type: WakeLockType): WakeLockSentinel {
    const sentinel = {
        released: true,
        type,
        onrelease: null as ((this: WakeLockSentinel, ev: Event) => unknown) | null,
        release: async () => {},
        addEventListener: () => {},
        removeEventListener: () => {},
        dispatchEvent: () => false,
    };
    return sentinel as WakeLockSentinel;
}

function createNonFatalRequest(originalRequest: WakeLock['request']) {
    return async (type: WakeLockType, options?: WakeLockOptions) => {
        try {
            return await originalRequest(type, options);
        } catch (error) {
            console.warn('[atemoya-contracts] Screen Wake Lock unavailable:', error);
            return createReleasedSentinel(type);
        }
    };
}

function patchWakeLockObject(wakeLock: WakeLock): WakeLock {
    if (!wakeLock || typeof wakeLock.request !== 'function') {
        return wakeLock;
    }

    const marked = wakeLock as WakeLock & { [PATCHED_WAKE_LOCK]?: boolean };
    if (marked[PATCHED_WAKE_LOCK]) {
        return wakeLock;
    }

    const originalRequest = wakeLock.request.bind(wakeLock);
    const patchedRequest = createNonFatalRequest(originalRequest);

    try {
        Object.defineProperty(wakeLock, 'request', {
            configurable: true,
            writable: true,
            value: patchedRequest,
        });
    } catch {
        try {
            wakeLock.request = patchedRequest;
        } catch {
            return wakeLock;
        }
    }

    marked[PATCHED_WAKE_LOCK] = true;
    return wakeLock;
}

export function installNonFatalScreenWakeLock(): void {
    if (installed || typeof navigator === 'undefined') {
        return;
    }

    const nav = navigator as Navigator & { wakeLock?: WakeLock };
    const ownDescriptor = Object.getOwnPropertyDescriptor(nav, 'wakeLock');
    const protoDescriptor = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(nav), 'wakeLock');
    const descriptor = ownDescriptor ?? protoDescriptor;

    if (descriptor?.get) {
        const originalGet = descriptor.get;
        Object.defineProperty(nav, 'wakeLock', {
            configurable: true,
            enumerable: descriptor.enumerable ?? true,
            get() {
                const wakeLock = originalGet.call(nav) as WakeLock;
                return patchWakeLockObject(wakeLock);
            },
        });
        installed = true;
        return;
    }

    if (nav.wakeLock) {
        patchWakeLockObject(nav.wakeLock);
        installed = true;
    }
}
